/* actionOutcome — what /api/ai/action/confirm actually told us.
 *
 * WHY THIS IS A FUNCTION AND NOT AN INLINE TERNARY.
 *
 * The confirm handler in useKira had two states: success and failure.
 * `data.success` chose between "Done." and "Couldn't complete that.",
 * stamped the action card as executed, fired kira_action_complete so the
 * module pages refetched, and played the celebration animation.
 *
 * PATCH /api/profile/bank stopped being a two-state endpoint. It no
 * longer writes bank details; it raises a WF04 approval request and
 * answers 202 (audit finding C5c). The write is ACCEPTED and NOT
 * APPLIED, and neither existing state describes that:
 *
 *   - as a success it celebrates and tells the employee their bank
 *     change is done, which is the exact implication the backend change
 *     removed from the endpoint's own response;
 *   - as a failure it says "that didn't go through" and invites a retry,
 *     which raises a SECOND pending request for the same change and
 *     leaves the reviewer unable to tell which one the employee meant.
 *
 * So there are three outcomes, and putting the choice in one pure
 * function is what makes it assertable — the repo's existing frontend
 * test (components/admin/workflow/graph.test.js) is pure-function style
 * because there is no @testing-library installed to render with.
 *
 * SCOPE: this reads the confirm response only. It deliberately knows
 * nothing about which action_type was run — the backend says whether a
 * write is pending, and any endpoint that starts answering 202 inherits
 * this without a change here.
 */

export const OUTCOME_DONE = "done";
export const OUTCOME_PENDING = "pending";
export const OUTCOME_FAILED = "failed";

/* The card should still be stamped "submitted" for a pending result —
 * the user did submit it — but nothing should claim the underlying data
 * changed, because it has not. */
export function actionOutcome(data) {
  const d = data || {};

  if (!d.success) {
    return {
      kind: OUTCOME_FAILED,
      reply: d.reply || d.error || "That didn't go through. Want to try again?",
      stampCard: false,
      refreshModules: false,
      celebrate: false,
    };
  }

  if (d.awaiting_approval) {
    return {
      kind: OUTCOME_PENDING,
      // The backend sends the endpoint's own sentence so the wording
      // lives with the rule. The fallback is only for an older server.
      reply:
        d.reply ||
        "That's been submitted for approval. It won't take effect until it's reviewed.",
      stampCard: true,
      // NOT refreshed: nothing changed, and a refetch that shows the OLD
      // bank details right after a "submitted" message reads as the save
      // having silently failed.
      refreshModules: false,
      // NOT celebrated: the animation is the strongest "it's done" signal
      // in the UI and this is the one case where it is not done.
      celebrate: false,
    };
  }

  return {
    kind: OUTCOME_DONE,
    reply: d.reply || "Done. Anything else I can help with?",
    stampCard: true,
    refreshModules: true,
    celebrate: true,
  };
}
